import { REGIONS } from '../types';

/**
 * Maps a county string (from Nominatim) to one of our REGIONS values.
 * 
 * Swedish counties often end with " län" (e.g., "Stockholms län")
 * but our REGIONS use landscape names (e.g., "Uppland", "Södermanland").
 * 
 * This function tries:
 * 1. Exact match (case-insensitive)
 * 2. Match without " län" suffix
 * 3. Known mappings from län to landskap
 */

// Mapping from Swedish län (counties) to landskaps (historical regions)
const LAN_TO_LANDSKAP: Record<string, string> = {
  'stockholms län': 'Uppland',
  'stockholms': 'Uppland',
  'uppsala län': 'Uppland',
  'uppsala': 'Uppland',
  'södermanlands län': 'Södermanland',
  'södermanlands': 'Södermanland',
  'östergötlands län': 'Östergötland',
  'östergötlands': 'Östergötland',
  'jönköpings län': 'Småland',
  'jönköpings': 'Småland',
  'kronobergs län': 'Småland',
  'kronobergs': 'Småland',
  'kalmar län': 'Småland',
  'kalmar': 'Småland',
  'gotlands län': 'Gotland',
  'gotlands': 'Gotland',
  'blekinge län': 'Blekinge',
  'blekinge': 'Blekinge',
  'skåne län': 'Skåne',
  'skåne': 'Skåne',
  'hallands län': 'Halland',
  'hallands': 'Halland',
  'västra götalands län': 'Västergötland',
  'västra götalands': 'Västergötland',
  'västra götaland': 'Västergötland',
  'värmlands län': 'Värmland',
  'värmlands': 'Värmland',
  'örebro län': 'Närke',
  'örebro': 'Närke',
  'västmanlands län': 'Västmanland',
  'västmanlands': 'Västmanland',
  'dalarnas län': 'Dalarna',
  'dalarnas': 'Dalarna',
  'dalarna': 'Dalarna',
  'gävleborgs län': 'Gästrikland',
  'gävleborgs': 'Gästrikland',
  'västernorrlands län': 'Medelpad',
  'västernorrlands': 'Medelpad',
  'jämtlands län': 'Jämtland',
  'jämtlands': 'Jämtland',
  'västerbottens län': 'Västerbotten',
  'västerbottens': 'Västerbotten',
  'norrbottens län': 'Norrbotten',
  'norrbottens': 'Norrbotten',
};

export function matchRegion(county: string | undefined): string | null {
  if (!county) return null;

  const normalized = county.toLowerCase().trim();

  // 1. Check exact match against REGIONS (case-insensitive)
  const exactMatch = REGIONS.find(
    (r) => r.toLowerCase() === normalized
  );
  if (exactMatch) return exactMatch;

  // 2. Remove " län" suffix and try again
  const withoutLan = normalized.replace(/\s*län$/i, '').trim();
  const matchWithoutLan = REGIONS.find(
    (r) => r.toLowerCase() === withoutLan
  );
  if (matchWithoutLan) return matchWithoutLan;

  // 3. Try known län-to-landskap mappings
  const mappedValue = LAN_TO_LANDSKAP[normalized] || LAN_TO_LANDSKAP[withoutLan];
  if (mappedValue && REGIONS.includes(mappedValue)) {
    return mappedValue;
  }

  // 4. Fuzzy: check if any region contains the county name or vice versa
  for (const region of REGIONS) {
    const regionLower = region.toLowerCase();
    if (withoutLan.includes(regionLower) || regionLower.includes(withoutLan)) {
      return region;
    }
  }

  return null;
}
